import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import type { CampaignUpsertInput } from '@/lib/campaigns'
import {
  listMissingTemplate3Fields,
  type Template3MissingFieldKey,
} from '@/lib/template3-campaign'
import { Template3CampaignFields } from './Template3CampaignFields'
import styles from './Template3MissingContentModal.module.css'

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"]):not([type="file"])',
  'textarea:not([disabled])',
  'select:not([disabled])',
].join(',')

type Template3MissingContentModalProps = {
  form: CampaignUpsertInput
  isSubmitting: boolean
  serverError?: string | null
  onClose: () => void
  onSave: (draft: CampaignUpsertInput) => void
  imageAccept: string
  validateFileSize: (file: File, maxBytes: number, label: string) => string | null
  maxImageBytes: number
}

export function Template3MissingContentModal({
  form,
  isSubmitting,
  serverError = null,
  onClose,
  onSave,
  imageAccept,
  validateFileSize,
  maxImageBytes,
}: Template3MissingContentModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const [draft, setDraft] = useState<CampaignUpsertInput>(form)
  const [localError, setLocalError] = useState<string | null>(null)
  const [hideServerError, setHideServerError] = useState(false)
  const [initialVisibleSections] = useState<Template3MissingFieldKey[] | undefined>(() => {
    const keys = listMissingTemplate3Fields(form).map((field) => field.key)
    return keys.length > 0 ? keys : undefined
  })
  const visibleSections =
    serverError && listMissingTemplate3Fields(draft).length === 0
      ? undefined
      : initialVisibleSections

  const requestClose = useCallback(() => {
    if (isSubmitting) {
      return
    }
    onClose()
  }, [isSubmitting, onClose])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const firstField = dialogRef.current
      ?.querySelector('[data-template3-missing-body]')
      ?.querySelector<HTMLElement>('input:not([type="file"]):not([type="hidden"]), textarea, button')
    firstField?.focus()

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        requestClose()
        return
      }

      if (event.key !== 'Tab') {
        return
      }

      const dialog = dialogRef.current
      if (!dialog) {
        return
      }

      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => element.getClientRects().length > 0
      )
      if (focusable.length === 0) {
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) {
        return
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [requestClose])

  const handleDraftChange = useCallback((patch: Partial<CampaignUpsertInput>) => {
    setDraft((current) => ({ ...current, ...patch }))
    setLocalError(null)
    setHideServerError(true)
  }, [])

  const handleFileError = useCallback((message: string | null) => {
    setLocalError(message)
    if (message) {
      setHideServerError(true)
    }
  }, [])

  const handleSave = useCallback(() => {
    if (isSubmitting) {
      return
    }
    const stillMissing = listMissingTemplate3Fields(draft)
    if (stillMissing.length > 0) {
      setLocalError(stillMissing[0].message)
      return
    }
    onSave(draft)
  }, [draft, isSubmitting, onSave])

  const errorMessage = localError ?? (hideServerError ? null : serverError)

  return createPortal(
    <div className={styles.overlay} role="presentation" onClick={requestClose}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="template3-missing-title"
        aria-describedby="template3-missing-subtitle"
        onClick={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.headerIcon} aria-hidden>
            <Icon icon="mdi:image-text" />
          </div>
          <div>
            <h2 id="template3-missing-title" className={styles.title}>
              Complete o Template 3
            </h2>
            <p id="template3-missing-subtitle" className={styles.subtitle}>
              Preencha só o que falta. O restante da campanha será salvo junto.
            </p>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={requestClose}
            disabled={isSubmitting}
            aria-label="Fechar"
          >
            <Icon icon="mdi:close" />
          </button>
        </header>

        <div className={styles.body} data-template3-missing-body>
          {errorMessage ? (
            <div className={styles.error} role="alert">
              {errorMessage}
            </div>
          ) : null}
          <Template3CampaignFields
            form={draft}
            onChange={handleDraftChange}
            onFileError={handleFileError}
            imageAccept={imageAccept}
            validateFileSize={validateFileSize}
            maxImageBytes={maxImageBytes}
            visibleSections={visibleSections}
            idPrefix="template3-missing"
            hideIntro
          />
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={requestClose}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleSave}
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  )
}
